import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ProductsApiEndpointService } from '@nibtravel/shared/util/url';
import { keyBy } from 'lodash-es';
import { catchError, map, reduce } from 'rxjs/operators';

import {
  IQuoteConfig,
  IQuoteConfigWithMaps,
} from './models/quote-config.interfaces';
import { Observable, merge, of } from 'rxjs';
import { QuoteType } from '@nibtravel/shared/util/models';
import { QuoteConfigStateModel } from './models/quote-config.interfaces';

@Injectable()
export class QuoteConfigDataService {
  constructor(
    private http: HttpClient,
    private productEndpointService: ProductsApiEndpointService
  ) {}

  /**
   *
   * @param brand bandcode
   *
   * @description fetches quoteConfig using products api for all quote types
   */
  getQuoteConfig(brand: string): Observable<QuoteConfigStateModel> {
    const singleTripQuoteConfig = this.getBaseQuoteConfig(
      brand,
      QuoteType.SingleTrip
    ).pipe(map((quoteConfig) => ({ [QuoteType.SingleTrip]: quoteConfig })));

    const multipleTripQuoteConfig = this.getBaseQuoteConfig(
      brand,
      QuoteType.MultiTrip
    ).pipe(map((quoteConfig) => ({ [QuoteType.MultiTrip]: quoteConfig })));

    return merge(singleTripQuoteConfig, multipleTripQuoteConfig).pipe(
      reduce<Record<string, IQuoteConfigWithMaps>, QuoteConfigStateModel>(
        (quoteConfigMap, quoteConfig) => ({
          ...quoteConfigMap,
          ...quoteConfig,
        }),
        {}
      )
    );
  }

  /**
   * @description
   * The availability of the configs depends on the effectivity and/or
   * existence of the plan for specific trip type to exist on the backend
   * When it doesn't exist, the backend throws a 404.
   * Since we are merging the calls, when one config fails, we catch the
   * exception and return an empty object.
   *
   * TODO: We might need to push logs to Sumo
   *
   * @param brand bandcode
   * @param quoteType single trip or multiple trip
   *
   */
  private getBaseQuoteConfig(
    brand: string,
    quoteType: QuoteType
  ): Observable<IQuoteConfigWithMaps> {
    const configPath = this.productEndpointService.getBaseQuoteConfigUrl(brand);
    const params = new HttpParams().set('quoteType', quoteType);
    return this.http.get<IQuoteConfig>(configPath, { params }).pipe(
      map((data) => this.convertObjectToMap(data)),
      catchError((_) => of({} as IQuoteConfigWithMaps))
    );
  }

  private convertObjectToMap(data: IQuoteConfig): IQuoteConfigWithMaps {
    const toMap = (source: any[]) => keyBy(source, (o) => o.homeCountryCode);
    const policyHolderAgeConfigsMap = toMap(data.policyHolderAgeConfigs);
    const travelDurationConfigsMap = toMap(data.travelDurationConfigs);
    const travelRestrictionConfigsMap = toMap(data.travelRestrictionConfigs);

    return {
      ...data,
      travelDurationConfigsMap,
      policyHolderAgeConfigsMap,
      travelRestrictionConfigsMap,
    };
  }
}
